import isEmpty from 'lodash/isEmpty';
import { ELEMENT_STATE } from '@/constants/element/constants';
import store from '@/store';

export const maxAttachmentsSize = 20 * 1024 * 1024; // 20Mb.

export default class EcsEmail {
  constructor(event, signature) {
    if (event && event.interactionId) {
      if (event.emailData) {
        event.emailData.from = parseEmails(event.emailData.from);
        event.emailData.cc = parseEmails(event.emailData.cc);
      }
      if (event.interactionMediaType) {
        // Email created from an interaction event
        Object.assign(this, this._createEmailFromEvent(event, signature));
      } else {
        // Email created from an already established email
        Object.assign(this, this._createEmail(event));
      }
    }
  }

  _createEmail({ interactionId, outgoingEmailData, incomingEmailData, contactId }) {
    return {
      interactionId,
      outgoingEmailData: outgoingEmailData || {
        bcc: '',
        requiredBcc: '',
        body: '',
        cc: '',
        creationTime: '',
        subject: '',
        from: '',
        to: '',
        attachments: [],
        canceled: false,
        signatureIndex: 1,
        signature: ''
      },
      incomingEmailData: incomingEmailData || {},
      contactId: contactId || ''
    };
  }

  _createEmailFromEvent({ interactionId, emailData }, signature) {
    let data = {};
    let subject = '';
    if (emailData) {
      data = emailData;
      subject = `RE: ${emailData.subject}`;
    }
    return {
      interactionId,
      outgoingEmailData: {
        bcc: data.bcc || '',
        requiredBcc: '',
        body: '',
        cc: data.cc || '',
        creationTime: '',
        subject,
        from: '',
        to: data.from || '',
        attachments: [],
        canceled: false,
        signature,
        signatureIndex: 1
      },
      incomingEmailData: data
    };
  }

  get isIncomingEmail() {
    return !isEmpty(this.incomingEmailData);
  }

  get areToEmailsValid() {
    if (this.outgoingEmailData) {
      const isEmpty = validateStringIsNotEmpty(this.outgoingEmailData.to);
      if (typeof isEmpty === 'string') {
        return isEmpty;
      }

      return validateEmails(this.outgoingEmailData.to);
    }
    return false;
  }

  get areCcEmailsValid() {
    if (this.outgoingEmailData) {
      return validateEmails(this.outgoingEmailData.cc);
    }
    return false;
  }

  get areBccEmailsValid() {
    if (this.outgoingEmailData) {
      return validateEmails(this.outgoingEmailData.bcc);
    }
    return false;
  }

  get isSubjectValid() {
    if (this.outgoingEmailData) {
      return validateStringIsNotEmpty(this.outgoingEmailData.subject);
    }
    return false;
  }

  get isReplying() {
    const elements = store.getters['ELEMENTS/getRawElementsForInteraction']({ interactionId: this.interactionId });
    return elements.some(element => element.elementStateId === ELEMENT_STATE.created);
  }

  get isFormValid() {
    return (
      typeof this.areToEmailsValid === 'boolean' &&
      typeof this.areCcEmailsValid === 'boolean' &&
      typeof this.areBccEmailsValid === 'boolean' &&
      typeof this.isSubjectValid === 'boolean'
    );
  }

  get validAttachmentsCurrentSize() {
    return this.outgoingEmailData.attachments.reduce(
      (accum, attachment) => (accum = attachment.error ? accum : accum + attachment.file.size),
      0
    );
  }

  get totalAttachmentsCurrentSize() {
    return this.outgoingEmailData.attachments.reduce((accum, attachment) => accum + attachment.file.size, 0);
  }

  get attachmentsHaveValidSize() {
    // Current maximum attachment total size is 4MB
    return this.totalAttachmentsCurrentSize < maxAttachmentsSize;
  }
}

export const parseEmails = emails => {
  if (!emails) {
    return '';
  }
  return emails.match(parseEmailRegExp).join('; ');
};

export const validateEmails = emails => {
  const validatedEmails = emails.split(';').map(email => {
    const aux = email.trim();
    if (!aux) {
      return true;
    }
    return emailsRegExp.test(aux);
  });
  const result = validatedEmails.reduce((previous, current) => current && previous, true);
  return result || 'invalidEmail';
};

const validateStringIsNotEmpty = value => !!value.length || 'fieldIsEmpty';

// eslint-disable-next-line
export const parseEmailRegExp = /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/g;
// eslint-disable-next-line
export const emailsRegExp = new RegExp(/(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/);
